import { renderMarkdown, type ContentDoc } from '@dia/content';
import { useT } from '@dia/i18n';
import { bodyFor, highlightPlaceholders } from '../logic/bilingual';

/**
 * Renders one content page in the reader's language.
 *
 * The renderer escapes every piece of text and refuses raw HTML, and the source
 * is a file in this repo, not user input, so setting innerHTML is safe here.
 * Typography is written as Tailwind variants on the wrapper because the
 * renderer emits plain h2, p, ul and ol with no classes.
 */
const PROSE =
  'flex flex-col gap-4 text-inkMuted leading-relaxed ' +
  '[&_h2]:font-display [&_h2]:text-ink [&_h2]:text-[1.25em] [&_h2]:mt-4 ' +
  '[&_h3]:font-display [&_h3]:text-ink [&_h3]:text-[1.1em] ' +
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2 ' +
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:flex [&_ol]:flex-col [&_ol]:gap-2 ' +
  '[&_a]:text-accent [&_a]:underline [&_a]:underline-offset-4 ' +
  '[&_strong]:text-ink [&_strong]:font-medium ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-accent [&_blockquote]:pl-4 ' +
  '[&_hr]:border-line';

export function Prose({ doc }: { doc: ContentDoc }) {
  const { lang } = useT();
  const html = highlightPlaceholders(renderMarkdown(bodyFor(doc.body, lang)));
  return <div className={PROSE} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** A page as an article: title, one line under it, then the body. */
export function DocArticle({
  doc,
  level = 2,
  className,
}: {
  doc: ContentDoc;
  level?: 1 | 2 | 3;
  className?: string;
}) {
  const { lang } = useT();
  const Heading = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
  return (
    <article className={className}>
      <Heading className="font-display text-ink text-[1.5em] leading-snug">{doc.title[lang]}</Heading>
      {doc.lede && <p className="text-inkFaint text-caption mt-1 mb-4">{doc.lede[lang]}</p>}
      <Prose doc={doc} />
    </article>
  );
}

/** A visible marker for a fact or a photo that is still missing, in code rather than in a page. */
export function Todo({ children }: { children: string }) {
  return (
    <mark className="rounded-sm border border-dashed border-mark bg-transparent px-1 text-mark">
      [{children}]
    </mark>
  );
}
